/********************************/
/*          About vuex          */
/********************************/
// The MIT License (MIT)
//
// Copyright (c) 2015-present Evan You
//
// Permission is hereby granted, free of charge, to any person obtaining a copy
// of this software and associated documentation files (the "Software"), to deal
// in the Software without restriction, including without limitation the rights
// to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
// copies of the Software, and to permit persons to whom the Software is
// furnished to do so, subject to the following conditions:
//
// The above copyright notice and this permission notice shall be included in
// all copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
// FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
// AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
// LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
// OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
// THE SOFTWARE.

import { mapState, mapActions } from 'vuex';
import api from '../../../common/api';
import config from '../../../share/config';
import handler from '../../../share/util/apiResultValidator';
import exam from '../../../share/util/inputValidator';
import util from '../../../share/util/utils';
import diagnoResource from '../../../share/util/diagnose';
import vehicle from '../../../share/util/vehicle';

export default {
  name: 'memo',
  props: {
    projectId: { type: String, required: true, },
    measureId: { type: String, required: true, },
    // APIの向け先(デフォルト値はダイアグ)
    destinationAPI: { type: String, required: false, default: config.DIAG },
    // 機能名のキー(デフォルトは全ダイアグコード)
    functionNameKey: { type: String, required: false, default: 'message.label_vehicle_system_tab_dtc' },
    obdFunctionLabelKey: { type: String, required: false, default: '' },
    modalLoading: { type: Boolean, required: true },
    isProjectVehicleName: { type: Boolean, required: false, default: false },
  },
  data() {
    return {
      isAlert: {},
      vin: null,
      vehicleName: null,
      hideRegistrationNumber: true,
      registrationNumber: null,
      displayProjectID: this.projectId,
      diagnoseComment: '',
      functionName: null,
      dataName: '',
      memo: ''
    };
  },

  computed: {
    ...mapState({
      vehicleModelDisplayNameList: state => state.masterResources.vehicleModelDisplayNameList,
      language: state => state.systems.language,
      cachedProjectDataSource: state => state.cache.projectDataSource,
    }),
    // 診断系のAPIは複数画面から使用されているため
    // 現在の画面で使用されるAPIが診断/データ再生/個別診断のうちどの機能なのか判別する
    operationScreen() {
      return diagnoResource.operationFrom(this.$router.currentRoute.value.name);
    },
    // 保存データ画面か否かの判定
    hasProjectEditLimit() {
      return this.operationScreen === config.EXECUE_FUNCTION_TYPE_DATAPLAYBACK;
    }
  },

  methods: {
    ...mapActions('auth', ['logout']),

    // メモ情報の保存
    async save() {
      // 入力の検証
      if (!this.validate()) return null;
      // 編集された情報を保存
      this.$emit('update-loading', true);
      const endPoint = '/projects/' + this.projectId + '/' + this.measureId;
      let res = null;
      res = await api.postCall(this.destinationAPI, endPoint, this.getRequestBody(), this.operationScreen);
      this.$emit('update-loading', false);
      let ret = null;
      // APIレスポンスの検証
      handler.validate(handler.validateTypes.all, res, this, null, () => {
        // 正常応答コールバック
        // 親の関数を呼び出し
        this.$emit('closeNoteModal');
        // 変更後の設定値を呼び出し元に返す
        ret = { comment: this.diagnoseComment, dataName: this.dataName, memo: this.memo };
      }, (result) => {
        // レスポンス不正時のコールバック
        if (result.behaviorType === handler.behaviorTypes.logout) this.logout(this);
      });
      return ret;
    },

    // メモ情報のクリア
    clear: function () {
      this.diagnoseComment = '';
      this.dataName = '';
      this.memo = '';
    },

    validate() {
      // 検証結果キャッシュの初期化
      this.clearAlert();
      const resultPool = [];

      // データ名の必須チェック
      this.isAlert.dataName = exam.excuteValidateCommons(this.dataName, true, 0, config.MEMOINFO_DETA_NAME_MAX_LEN);
      resultPool.push(this.isAlert.dataName.valid);

      // 診断コメントの文字数チェック
      this.isAlert.diagnoseComment = exam.excuteValidateCommons(this.diagnoseComment, false, 0, config.MEMOINFO_MAX_LEN);
      resultPool.push(this.isAlert.diagnoseComment.valid);

      // メモの文字数チェック
      this.isAlert.memo = exam.excuteValidateCommons(this.memo, false, 0, config.MEMOINFO_MAX_LEN);
      resultPool.push(this.isAlert.memo.valid);


      const faults = resultPool.filter(el => el === false);
      return faults.length === 0;
    },

    getRequestBody: function () {
      const body = {
        name: this.projectID,
        comment: this.diagnoseComment,
        measured: {
          name: this.dataName,
          memo: this.memo
        }
      };
      // 保存データ（プロジェクト詳細画面、データ再生画面）のみ region_info を付与する
      if (this.hasProjectEditLimit) {
        body.region_info = this.cachedProjectDataSource;
      }

      return body;
    },

    // 入力検証アラートのクリア
    clearAlert: function () {
      this.isAlert = {
        dataName: { valid: null, msg: '' },
        diagnoseComment: { valid: null, msg: '' },
        memo: { valid: null, msg: '' },
      };
    },

    // メモ情報表示
    async updateMemoInfo() {
      // メモ情報を取得
      this.$emit('update-loading', true);
      const endPoint = '/projects/' + this.projectId + '/' + this.measureId;
      const params = this.operationScreen === config.EXECUE_FUNCTION_TYPE_DATAPLAYBACK ? { region_info: this.cachedProjectDataSource } : null;
      let res = null;
      // wspはデータ再生でのみ使用されるため、this.operationScreen を設定しない。
      if (this.destinationAPI === config.WSP) {
        res = await api.getParamCall(this.destinationAPI, endPoint, params, null);
      } else {
        res = await api.getParamCall(this.destinationAPI, endPoint, params, this.operationScreen);
      }
      this.$emit('update-loading', false);
      // APIレスポンスの検証
      handler.validate(handler.validateTypes.all, res, this, null, () => {
        // メモ情報のセット
        const vcl = res.data.project;
        this.vin = vcl.vin.vin;

        const spec = vehicle.createModelDisplaySpec(vcl.vin.type, vcl.vin);
        // データ再生画面の場合、プロジェクト登録データのままの車両名を表示する
        this.vehicleName = this.isProjectVehicleName ? ('{0} {1}').format(util.isNullOrDefault(vcl.vin.vehicle_model, ''), spec)
          : vehicle.createModelDisplayNameCurrentLanguage(vcl.vin.vehicle_model, this.vehicleModelDisplayNameList, this.language, '{0} {1}', spec);
        // 海外VINの場合、登録番号は画面から非表示にする
        if (vcl.vin.type === config.VEHICLEINFO_TYPE_VIN) {
          this.hideRegistrationNumber = false;
        } else {
          this.registrationNumber = ('{0} {1} {2} {3}').format(
            util.isNullOrDefault(vcl.vin.area_name, ''),
            util.isNullOrDefault(vcl.vin.category, ''),
            util.isNullOrDefault(vcl.vin.hiragana, ''),
            util.isNullOrDefault(vcl.vin.individual_number, '')
          );
        }
        this.projectID = vcl.id;
        this.diagnoseComment = vcl.comment;
        if (this.obdFunctionLabelKey) {
          this.functionName = `${this.$t(this.functionNameKey)}${this.$t(this.obdFunctionLabelKey)}`;
        } else {
          this.functionName = this.$t(this.functionNameKey);
        }
        this.dataName = vcl.measured.data_name;
        this.memo = vcl.measured.memo;
      }, (result) => {
        // 処理が失敗したときのコールバック処理
        if (result.behaviorType === handler.behaviorTypes.logout) {
          this.logout(this);
        }
      });
    },

  },

  async created() {
    this.clearAlert();
    await this.updateMemoInfo();
  },

  mounted() {
    // noop
  },
};